import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2 } from "lucide-react";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [devUrl, setDevUrl] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            setSent(true);
            if (data.dev_reset_url) setDevUrl(data.dev_reset_url);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="forgot-password-page">
            <div className="border-2 border-ink p-8">
                <h1 className="font-serif text-3xl font-black">Reset your password</h1>
                <p className="mt-2 text-sm text-muted-foreground">Enter your account email and we'll send you a link to set a new password.</p>

                {sent ? (
                    <div className="mt-8" data-testid="forgot-success">
                        <div className="flex items-start gap-3 border border-pos p-4">
                            <CheckCircle2 className="text-pos shrink-0 mt-0.5" size={20} />
                            <p className="text-sm">If an account exists for that email, a password reset link has been sent.</p>
                        </div>
                        {devUrl && (
                            <div className="mt-4 border border-hair bg-muted p-4">
                                <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Demo mode — no email is sent</p>
                                <Link to={devUrl.replace(/^https?:\/\/[^/]+/, "")} data-testid="dev-reset-link" className="text-sm text-accent link-underline break-all">
                                    Continue to reset your password →
                                </Link>
                            </div>
                        )}
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-8 space-y-5">
                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Email</label>
                            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email"
                                className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                        </div>
                        {error && <p className="text-sm text-neg border border-neg p-3" data-testid="forgot-error">{error}</p>}
                        <button type="submit" disabled={loading} data-testid="forgot-submit"
                            className="w-full bg-ink text-white py-3 font-mono text-xs uppercase tracking-widest hover:bg-accent transition-colors duration-150 disabled:opacity-50">
                            {loading ? "Sending…" : "Send reset link"}
                        </button>
                    </form>
                )}

                <p className="mt-6 text-sm text-muted-foreground">
                    Remembered it? <Link to="/login" className="text-accent link-underline">Back to log in</Link>
                </p>
            </div>
        </div>
    );
}
